import { UiSchema } from '@rjsf/utils';
import { get } from 'lodash/fp';
import { Fragment, useMemo, VFC } from 'react';
import { useSelector } from 'react-redux';

import WorkflowActionDiffActionMeta from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/StepConfigAction/ActionDiffActionMeta';
import WorkflowActionDiffCheckbox from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/StepConfigAction/ActionDiffCheckbox';
import WorkflowActionDiffFieldSchemasOnTemplate from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/StepConfigAction/ActionDiffFieldSchemasOnTemplate';
import WorkflowActionDiffHandlebars from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/StepConfigAction/ActionDiffHandlebars';
import WorkflowActionDiffObject from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow/StepConfigAction/ActionDiffObject';
import { WorkflowVersionStepDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { ValueDiffLabel } from 'dashboard/apps/InquiryTemplateEditor/ValueDiffLabel';
import { PropertyRow } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import { getActionKeyToMetaDataMap } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/selectActionUtils';
import { getTokensToDisplayValue } from 'dashboard/lib/workflows';
import { WorkflowActionMeta } from 'dashboard/models/workflow';
import {
  GenericWorkflowAction,
  GenericWorkflowActionEnumType,
  UiSchemaFieldTypes,
  UiSchemaWidgetTypes,
} from 'dashboard/models/workflow-schema';
import {
  WorkflowActionFromConfig,
  WorkflowStepAction,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import { getUsableObjectsFLE } from 'dashboard/redux/selectors/fle-workflow-context';
import { RootState } from 'dashboard/redux/store';

interface ActionDifferProps {
  workflowVersionId: string;
  workflowAction: GenericWorkflowAction;
  actionParameters: WorkflowActionFromConfig['params'];
  step: WorkflowStepAction;
  actionMeta: WorkflowActionMeta;
  workflowStepDiff?: WorkflowVersionStepDiff;
}

const ActionDiffer: VFC<ActionDifferProps> = ({
  workflowVersionId,
  workflowAction,
  actionParameters,
  actionMeta,
  workflowStepDiff,
  step,
}) => {
  const usableObjects = useSelector((state: RootState) =>
    getUsableObjectsFLE(state, workflowVersionId, step.token),
  );
  const tokensToDisplayValue = useMemo(
    () => getTokensToDisplayValue(usableObjects),
    [usableObjects],
  );

  const actionMetaDataMap = getActionKeyToMetaDataMap();
  const getActionMetaDataLabel = (actionKey: string) => {
    if (actionKey === WorkflowStepActionKey.RunHttpPlatformIntegration) {
      return 'Run Platform Integration';
    } else {
      return actionMetaDataMap[actionKey.replace(/-/g, '_')].label;
    }
  };
  const actionKeyDiff = get(['changes', 'step', `action.key`], workflowStepDiff) || null;

  return (
    <>
      <PropertyRow
        textKey='Action'
        value={
          actionKeyDiff ? (
            <ValueDiffLabel
              valueDiff={{
                added: getActionMetaDataLabel(actionKeyDiff.value),
                removed: getActionMetaDataLabel(actionKeyDiff['prev-value']),
              }}
              fallbackValue='None'
              vertical
            />
          ) : (
            getActionMetaDataLabel(workflowAction.key)
          )
        }
      />

      {Object.entries(workflowAction.request.uiSchema).map(
        ([schemaKey, schema]: [schemaKey: string, schema: UiSchema]) => {
          // only render parameter changes if actionKey was not modified
          const parameterDiff = actionKeyDiff
            ? null
            : get(['changes', 'step', `action.params.${schemaKey}`], workflowStepDiff) || null;

          return (
            <Fragment key={schemaKey}>
              {schema['ui:widget'] === UiSchemaWidgetTypes.Select &&
                schema['ui:options']?.['enumType'] === GenericWorkflowActionEnumType.Object && (
                  <WorkflowActionDiffObject
                    actionParameters={actionParameters}
                    schema={schema}
                    schemaKey={schemaKey}
                    parameterDiff={parameterDiff}
                    usableObjects={usableObjects}
                  />
                )}
              {schema['ui:widget'] === UiSchemaWidgetTypes.Select &&
                schema['ui:options']?.['enumType'] === GenericWorkflowActionEnumType.Meta && (
                  <WorkflowActionDiffActionMeta
                    parameterDiff={parameterDiff}
                    actionParameters={actionParameters}
                    schema={schema}
                    schemaKey={schemaKey}
                    actionMeta={actionMeta}
                  />
                )}
              {schema['ui:field'] === UiSchemaFieldTypes.Handlebars && (
                <WorkflowActionDiffHandlebars
                  parameterDiff={parameterDiff}
                  actionParameters={actionParameters}
                  schema={schema}
                  schemaKey={schemaKey}
                  tokensToDisplayValue={tokensToDisplayValue}
                />
              )}
              {schema['ui:field'] === UiSchemaFieldTypes.FieldsMapping && (
                <WorkflowActionDiffFieldSchemasOnTemplate
                  tokensToDisplayValue={tokensToDisplayValue}
                  workflowStepDiff={workflowStepDiff}
                  schema={schema}
                  schemaKey={schemaKey}
                  actionParameters={actionParameters}
                />
              )}
              {schema['ui:field'] === UiSchemaFieldTypes.Checkbox && (
                <WorkflowActionDiffCheckbox
                  parameterDiff={parameterDiff}
                  schema={schema}
                  schemaKey={schemaKey}
                  actionParameters={actionParameters}
                />
              )}
            </Fragment>
          );
        },
      )}
    </>
  );
};

export default ActionDiffer;
